import { createAuthService } from "../firebase/auth";

/**
 * Security utility functions to verify user authentication and ownership
 */

const authService = createAuthService();

/**
 * Verifies that a user is authenticated
 * @throws Error if user is not authenticated
 */
export function requireAuth(): string {
  const userId = authService.getUserId();
  if (!userId) {
    throw new Error("Authentication required. Please log in to continue.");
  }
  return userId;
}

/**
 * Verifies that the current user owns the resource
 * @param resourceUserId - The user ID associated with the resource
 * @throws Error if user is not authenticated or doesn't own the resource
 */
export function requireOwnership(resourceUserId: string | null | undefined): void {
  const userId = requireAuth();
  if (!resourceUserId) {
    throw new Error("Resource not found or access denied.");
  }
  if (resourceUserId !== userId) {
    throw new Error("Access denied. You can only access your own resources.");
  }
}

/**
 * Verifies that the current user owns the resource or is an admin
 * @param resourceUserId - The user ID associated with the resource
 * @param isAdmin - Whether the current user is an admin
 * @throws Error if user is not authenticated or doesn't have permission
 */
export function requireOwnershipOrAdmin(resourceUserId: string | null | undefined, isAdmin: boolean): void {
  if (isAdmin) {
    requireAuth(); // Just verify authentication for admins
    return;
  }
  requireOwnership(resourceUserId);
}

/**
 * Gets the current user's ID token for API calls
 * @param forceRefresh - Whether to force refresh the token
 * @returns The ID token or null if not authenticated
 */
export async function getUserToken(forceRefresh: boolean = false): Promise<string | null> {
  return await authService.getIdToken(forceRefresh);
}

